// Calculation Utilities
// Pure functions for calculating subtotals, contingency, and totals.
// These functions are designed to be deterministic and side-effect free.

import { PropertyType } from './sow.types';
import type {
  CurrencyValue,
  CategoryData,
  SOWData,
  ContingencyData,
  FieldValues,
} from './sow.types';
import { sowConfig, getCategoryById, getCategoriesForPropertyType } from './sow.config';

// Sum all values in a FieldValues object
export function sumFieldValues(fieldValues: FieldValues): CurrencyValue {
  return Object.values(fieldValues).reduce((sum, value) => sum + (value || 0), 0);
}

// Calculate subtotal for a single category
// Includes building-wide values and unit values (for multi-unit only)
export function calculateCategorySubtotal(
  categoryData: CategoryData,
  propertyType: PropertyType
): CurrencyValue {
  let total = sumFieldValues(categoryData.buildingWide);

  // Only include unit values for multi-unit properties
  // This matches the behavior of categoryHasValues for consistency
  if (propertyType === PropertyType.MultiUnit) {
    for (const unitValues of categoryData.units) {
      total += sumFieldValues(unitValues);
    }
  }

  return total;
}

// Calculate the overall subtotal across all categories
export function calculateSubtotal(data: SOWData): CurrencyValue {
  let subtotal = 0;

  for (const [categoryId, categoryData] of Object.entries(data.categories)) {
    const category = getCategoryById(categoryId, data.propertyType);
    if (!category) continue;

    subtotal += calculateCategorySubtotal(categoryData, data.propertyType);
  }

  return subtotal;
}

// Calculate contingency amount based on subtotal
// Formula: max(percentage * subtotal, minimum)
export function calculateContingency(
  subtotal: CurrencyValue,
  percentage: number = sowConfig.contingencyPercentage,
  minimum: CurrencyValue = sowConfig.contingencyMinimum
): CurrencyValue {
  const percentageAmount = Math.round(subtotal * percentage);
  return Math.max(percentageAmount, minimum);
}

// Create a ContingencyData object with calculated value
export function createContingencyData(
  subtotal: CurrencyValue,
  currentValue?: CurrencyValue,
  currentMode: 'auto' | 'manual' = 'auto'
): ContingencyData {
  const calculatedValue = calculateContingency(subtotal);

  return {
    value: currentMode === 'auto' ? calculatedValue : (currentValue ?? calculatedValue),
    mode: currentMode,
    calculatedValue,
  };
}

// Calculate the grand total (subtotal + contingency)
export function calculateTotal(subtotal: CurrencyValue, contingencyValue: CurrencyValue): CurrencyValue {
  return subtotal + contingencyValue;
}

// Recalculate all totals in SOWData
// Returns a new SOWData object with updated totals
export function recalculateTotals(data: SOWData): SOWData {
  const subtotal = calculateSubtotal(data);
  const contingency = createContingencyData(
    subtotal,
    data.contingency.value,
    data.contingency.mode
  );
  const total = calculateTotal(subtotal, contingency.value);

  return {
    ...data,
    subtotal,
    contingency,
    total,
  };
}

//Check if a FieldValues object has any non-zero values
export function hasNonZeroValues(values: FieldValues): boolean {
  return Object.values(values).some(v => v > 0);
}

// Count non-zero values in a FieldValues object
export function countNonZeroValuesInFields(values: FieldValues): number {
  return Object.values(values).filter(v => v > 0).length;
}

// Check if a category has any non-zero values or description
export function categoryHasValues(
  categoryData: CategoryData,
  propertyType: PropertyType
): boolean {
  // Check building-wide values
  if (hasNonZeroValues(categoryData.buildingWide)) {
    return true;
  }

  // Check unit values for multi-unit
  if (propertyType === PropertyType.MultiUnit) {
    for (const unitValues of categoryData.units) {
      if (hasNonZeroValues(unitValues)) {
        return true;
      }
    }
  }

  // Check description
  if (categoryData.description?.trim()) {
    return true;
  }

  return false;
}

// Get count of non-zero fields in a category
export function countNonZeroFields(
  categoryData: CategoryData,
  propertyType: PropertyType
): number {
  let count = 0;

  // Count building-wide
  count += countNonZeroValuesInFields(categoryData.buildingWide);

  // Count unit values for multi-unit
  if (propertyType === PropertyType.MultiUnit) {
    for (const unitValues of categoryData.units) {
      count += countNonZeroValuesInFields(unitValues);
    }
  }

  return count;
}

// Calculate fill percentage for the form
// (number of filled fields / total possible fields)
export function calculateFillPercentage(data: SOWData): number {
  let filledCount = 0;
  let totalCount = 0;

  // Use property-type-specific categories to get correct field counts
  const categories = getCategoriesForPropertyType(data.propertyType);

  for (const category of categories) {
    const categoryData = data.categories[category.id];
    if (!categoryData) continue;

    const fieldCount = category.fields.length;

    if (category.scope === 'building-wide') {
      totalCount += fieldCount;
      filledCount += countNonZeroValuesInFields(categoryData.buildingWide);
    } else {
      // Per-unit: count building-wide column + all units
      const unitCount = data.propertyType === PropertyType.MultiUnit ? data.unitCount : 0;
      totalCount += fieldCount * (1 + unitCount); // 1 for building column

      filledCount += countNonZeroValuesInFields(categoryData.buildingWide);

      if (data.propertyType === PropertyType.MultiUnit) {
        for (const unitValues of categoryData.units) {
          filledCount += countNonZeroValuesInFields(unitValues);
        }
      }
    }
  }

  return totalCount > 0 ? (filledCount / totalCount) * 100 : 0;
}

